import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useTrackInterests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: interests = [], isLoading } = useQuery({
    queryKey: ["track-interests", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("track_interests" as any)
        .select("track_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data as any[]).map((r) => r.track_id as string);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) throw new Error("Not authenticated");
      // Read fresh state from cache to avoid stale closure
      const current = queryClient.getQueryData<string[]>(["track-interests", user.id]) ?? [];
      const hasInterest = current.includes(trackId);

      if (hasInterest) {
        const { error } = await supabase
          .from("track_interests" as any)
          .delete()
          .eq("user_id", user.id)
          .eq("track_id", trackId);
        if (error) throw error;
        return { added: false, trackId };
      } else {
        const { error } = await supabase
          .from("track_interests" as any)
          .insert({ user_id: user.id, track_id: trackId } as any);
        if (error) throw error;
        return { added: true, trackId };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["track-interests", user?.id] });
    },
  });

  return {
    interests,
    isLoading,
    toggleInterest: toggleMutation.mutateAsync,
    isToggling: toggleMutation.isPending,
  };
}
